import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  Shield, Plus, Edit, Trash2, Check, X, Loader2, Users, 
  Eye, FileText, CheckCircle, AlertCircle, Lock, Settings
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PERMISSION_LABELS = {
  view: { label: 'View', icon: Eye, color: 'blue' },
  add: { label: 'Add', icon: Plus, color: 'green' },
  edit: { label: 'Edit', icon: Edit, color: 'amber' },
  delete: { label: 'Delete', icon: Trash2, color: 'red' },
  approve: { label: 'Approve', icon: Check, color: 'purple' }
};

const MODULE_LABELS = {
  centers: 'Centers',
  students: 'Students',
  courses: 'Courses',
  fees: 'Fees',
  enquiries: 'Enquiries',
  staff: 'Staff',
  attendance: 'Attendance',
  exams: 'Exams',
  certificates: 'Certificates',
  jobs: 'Jobs',
  employers: 'Employers',
  reports: 'Reports'
};

const RoleManagement = () => {
  const [roles, setRoles] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    permissions: {}
  });

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/roles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRoles(res.data.roles || []);
      setModules(res.data.modules || []);
    } catch (error) {
      toast.error('Failed to fetch roles');
    } finally {
      setLoading(false);
    }
  };

  const initializePermissions = (existingPermissions = {}) => {
    const perms = {};
    Object.keys(MODULE_LABELS).forEach(module => {
      perms[module] = existingPermissions[module] || {
        view: false, add: false, edit: false, delete: false, approve: false
      };
    });
    return perms;
  };

  const openCreateDialog = () => {
    setEditingRole(null);
    setFormData({
      name: '',
      description: '',
      permissions: initializePermissions()
    });
    setShowDialog(true);
  };

  const openEditDialog = (role) => {
    setEditingRole(role);
    setFormData({
      name: role.name,
      description: role.description || '',
      permissions: initializePermissions(role.permissions)
    });
    setShowDialog(true);
  };

  const handlePermissionChange = (module, permission) => {
    setFormData(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [module]: {
          ...prev.permissions[module],
          [permission]: !prev.permissions[module]?.[permission]
        }
      }
    }));
  };

  const toggleAllModulePermissions = (module) => {
    const currentPerms = formData.permissions[module] || {};
    const allEnabled = Object.values(currentPerms).every(v => v);
    
    setFormData(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [module]: {
          view: !allEnabled,
          add: !allEnabled,
          edit: !allEnabled,
          delete: !allEnabled,
          approve: !allEnabled
        }
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Role name is required');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      if (editingRole) {
        await axios.put(`${API}/admin/roles/${editingRole.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Role updated successfully');
      } else {
        await axios.post(`${API}/admin/roles`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Role created successfully');
      }
      
      setShowDialog(false);
      fetchRoles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save role');
    }
  };

  const handleDelete = async (role) => {
    if (role.is_system_role) {
      toast.error('System roles cannot be deleted');
      return;
    }
    
    if (!confirm(`Are you sure you want to delete "${role.name}"?`)) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/roles/${role.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Role deleted successfully');
      fetchRoles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete role');
    }
  };

  const countPermissions = (permissions) => {
    let count = 0;
    Object.values(permissions || {}).forEach(module => {
      Object.values(module || {}).forEach(perm => {
        if (perm) count++;
      });
    });
    return count;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Shield className="w-7 h-7 text-blue-600" />
            Role Management
          </h2>
          <p className="text-slate-600 mt-1">Define roles and assign permissions for sub-admins</p>
        </div>
        <Button onClick={openCreateDialog} className="bg-blue-600 hover:bg-blue-700">
          <Plus className="w-4 h-4 mr-2" />
          Create New Role
        </Button>
      </div>

      {/* Roles Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {roles.map(role => (
          <div 
            key={role.id} 
            className={`bg-white rounded-xl border-2 p-5 transition-all hover:shadow-md ${
              role.is_system_role ? 'border-blue-200' : 'border-slate-200'
            }`}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${role.is_system_role ? 'bg-blue-100' : 'bg-slate-100'}`}>
                  <Shield className={`w-5 h-5 ${role.is_system_role ? 'text-blue-600' : 'text-slate-600'}`} />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-800">{role.name}</h3>
                  {role.is_system_role && (
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                      System Role
                    </span>
                  )}
                </div>
              </div>
              <div className="flex gap-1">
                <button 
                  onClick={() => openEditDialog(role)}
                  className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors"
                  title="Edit Role"
                >
                  <Edit className="w-4 h-4 text-slate-500" />
                </button>
                {!role.is_system_role && (
                  <button 
                    onClick={() => handleDelete(role)}
                    className="p-1.5 hover:bg-red-50 rounded-lg transition-colors"
                    title="Delete Role"
                  >
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </button>
                )}
              </div>
            </div>
            
            <p className="text-sm text-slate-500 mb-4 line-clamp-2">
              {role.description || 'No description'}
            </p>
            
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-600">
                {countPermissions(role.permissions)} permissions
              </span>
              <button 
                onClick={() => openEditDialog(role)}
                className="text-blue-600 hover:text-blue-700 flex items-center gap-1"
              >
                <Settings className="w-4 h-4" />
                Configure
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-blue-600" />
              {editingRole ? 'Edit Role' : 'Create New Role'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Info */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">Role Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Regional Manager"
                  className="mt-1"
                  disabled={editingRole?.is_system_role}
                />
              </div>
              <div>
                <Label htmlFor="description">Description</Label>
                <Input
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of the role"
                  className="mt-1"
                />
              </div>
            </div>

            {/* Permissions Grid */}
            <div>
              <Label className="text-lg font-semibold">Module Permissions</Label>
              <p className="text-sm text-slate-500 mb-4">Configure access for each module</p>
              
              <div className="bg-slate-50 rounded-xl border border-slate-200 overflow-hidden">
                {/* Header */}
                <div className="grid grid-cols-7 gap-2 p-3 bg-slate-100 border-b border-slate-200 text-sm font-medium text-slate-700">
                  <div className="col-span-2">Module</div>
                  {Object.entries(PERMISSION_LABELS).map(([key, { label }]) => (
                    <div key={key} className="text-center">{label}</div>
                  ))}
                </div>
                
                {/* Rows */}
                {Object.entries(MODULE_LABELS).map(([moduleKey, moduleLabel]) => (
                  <div 
                    key={moduleKey} 
                    className="grid grid-cols-7 gap-2 p-3 border-b border-slate-100 hover:bg-white transition-colors items-center"
                  >
                    <div className="col-span-2 flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => toggleAllModulePermissions(moduleKey)}
                        className="text-xs text-blue-600 hover:text-blue-700"
                        title="Toggle all"
                      >
                        <CheckCircle className="w-4 h-4" />
                      </button>
                      <span className="font-medium text-slate-700">{moduleLabel}</span>
                    </div>
                    {Object.entries(PERMISSION_LABELS).map(([permKey, { color }]) => (
                      <div key={permKey} className="text-center">
                        <button
                          type="button"
                          onClick={() => handlePermissionChange(moduleKey, permKey)}
                          className={`w-8 h-8 rounded-lg border-2 flex items-center justify-center transition-all ${
                            formData.permissions[moduleKey]?.[permKey]
                              ? `bg-${color}-100 border-${color}-500 text-${color}-600`
                              : 'bg-white border-slate-200 text-slate-300 hover:border-slate-300'
                          }`}
                        >
                          {formData.permissions[moduleKey]?.[permKey] ? (
                            <Check className="w-4 h-4" />
                          ) : (
                            <X className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                {editingRole ? 'Update Role' : 'Create Role'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RoleManagement;
